import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Media } from '../ui/Media';
import { Icon } from '../ui/Icon';
import { Accordéon, Bouton, CaseACocher, Compteur, Note, Segmente, Vide, ZoneTexte, cn, useToasts } from '../ui/kit';
import { Fil, clsBouton } from './partages';
import { ProductCard } from '../components/ProductCard';
import { useBoutique } from '../state/boutique';
import {
  arrhes, eur, formatLePlusProche, kg, poidsParDefaut, prixPour, produit as trouverProduit,
  produits, produitsSimilaires
} from '../data/repository';
import { AVIS, REGLAGES, type Produit } from '../data/mock-data';

/**
 * FICHE PRODUIT — l'écran qui décide de l'achat.
 *
 * Hiérarchie, de haut en bas : ce que c'est → combien ça coûte → combien j'en veux
 * → pourquoi je peux avoir confiance. Le sélecteur (poids / personnes / pièce) est
 * un **choix**, pas un bouton : il vit dans un panneau teinté, et l'action d'achat
 * reste la seule tâche rouge de l'écran.
 */
const NOMS_TEMPERATURE: Record<string, string> = {
  aPoint: 'à cœur',
  saignant: 'saignant',
  aPointe: 'à point',
  bienCuit: 'bien cuit',
};

const NOMS_OPTIONS: Record<string, string> = {
  EPAISSEUR: 'Épaisseur de coupe',
  PREPARATION: 'Préparation',
  MARINADE: 'Marinade',
  EMBALLAGE: 'Emballage',
};

export function ProduitDetail() {
  const { slug } = useParams<{ slug: string }>();
  const p = slug ? trouverProduit(slug) : undefined;

  if (!p) {
    return (
      <main id="contenu" className="mx-auto max-w-3xl px-5 py-16">
        <Vide icone="package" titre="Cette pièce n’est plus au comptoir"
          texte="Elle a peut-être changé de nom, ou elle est saisonnière. Le reste du catalogue vous attend."
          action={<Link to="/boutique" className={clsBouton('ink')}>Retour au comptoir</Link>} />
      </main>
    );
  }
  /* La clé force une fiche neuve par produit : les valeurs par défaut viennent de la
     règle publiée du produit, jamais d'un état hérité de la fiche précédente. */
  return <FicheProduit key={p.id} p={p} />;
}

function FicheProduit({ p }: { p: Produit }) {
  const { ajouter, ouvrirPanier, favoris, basculerFavori, mode } = useBoutique();
  const { annoncer } = useToasts();

  const [modeChoix, setModeChoix] = useState<'POIDS' | 'PERSONNES' | 'PIECE'>(
    p.modele !== 'AU_KG' ? 'PIECE' : p.portion.modeDefaut === 'PERSONNES' ? 'PERSONNES' : 'POIDS'
  );
  const [poids, setPoids] = useState(() => poidsParDefaut(p));
  const [personnes, setPersonnes] = useState(() =>
    Math.max(1, Math.round(poidsParDefaut(p) / (p.portion.gParPersonne || 200))));
  const [quantite, setQuantite] = useState(1);
  const [appetit, setAppetit] = useState<'PETIT' | 'NORMAL' | 'GROS'>('NORMAL');
  const [enfants, setEnfants] = useState(0);
  const [note, setNote] = useState('');
  const [options, setOptions] = useState<Record<string, string>>(() =>
    Object.fromEntries(p.options.map(o => [o.cle, o.valeurs[0]])));
  const [vue, setVue] = useState(0);
  const similaires = useMemo(() => produitsSimilaires(p, 4), [p]);

  /* Les avis ne se mélangent pas : on affiche d'abord ceux de la pièce, puis ceux
     du rayon. Un avis sur la merguez n'a rien à faire sous une côte de bœuf. */
  const avisDuRayon = useMemo(() => {
    const duRayon = produits().filter(x => x.famille === p.famille);
    const slugs = new Set([p.slug, ...duRayon.map(x => x.slug)]);
    return AVIS.filter(a => slugs.has(a.produit));
  }, [p]);
  const nomParSlug = useMemo(() => new Map(produits().map(x => [x.slug, x.nom])), []);

  /* Aucune fiche sans mentions légales : même sans allergène déclaré, l'accordéon
     existe et le dit. Un vide assumé vaut mieux qu'une rubrique absente. */
  const aDesMentions = true;

  const estPiece = p.modele === 'PRIX_FIXE' || p.modele === 'COLIS';
  const estFormats = p.modele === 'FORMATS';

  /* Poids « par personne » : convives − enfants (½ part) × grammage de la règle
     × appétit, arrondi **au pas supérieur** puis borné. C'est le même calcul que
     le serveur (§6.4) : l'écran annonce, il ne décide pas. */
  const FACTEURS_APPETIT = { PETIT: 0.8, NORMAL: 1, GROS: 1.25 } as const;
  const poidsPourPersonnes = (n: number, enf: number, app: keyof typeof FACTEURS_APPETIT) => {
    const parts = Math.max(0.5, (n - enf) + enf * 0.5);
    const pas = p.portion.pasG || 50;
    const brut = Math.ceil((parts * (p.portion.gParPersonne || 200) * FACTEURS_APPETIT[app]) / pas) * pas;
    return Math.min(Math.max(brut, p.portion.minG || 0), p.portion.maxG || 1e6);
  };
  const poidsEffectif = modeChoix === 'PERSONNES'
    ? poidsPourPersonnes(personnes, enfants, appetit)
    : estPiece
      ? (p.portion.poidsReferenceG ?? p.poidsG ?? 0)
      : poids;

  /* Un produit en formats suit le format choisi, pas le curseur. */
  const formatChoisi = estFormats ? formatLePlusProche(p, poidsEffectif) : undefined;
  const poidsFinal = formatChoisi ? formatChoisi.poidsG : poidsEffectif;
  const prixUnitaire = estFormats ? (formatChoisi?.prix ?? 0) : prixPour(p, poidsFinal);
  const total = Math.round(prixUnitaire * quantite * 100) / 100;
  const ar = arrhes(total, mode);
  const favori = favoris.includes(p.id);

  const photos = p.photos.length > 0 ? p.photos : [
    { url: '', alt: `${p.nom}, vue crue`, legende: 'Cru' },
    { url: '', alt: `${p.nom}, sur planche`, legende: 'Planche' },
    { url: '', alt: `${p.nom}, suggestion de service`, legende: 'Service' },
  ];

  const ajouterAuPanier = () => {
    ajouter(p, { poidsG: poidsFinal, quantite, options, note });
    annoncer({
      texte: `${p.nom} ajouté au panier — ${estPiece ? 'à la pièce' : kg(poidsFinal)}.`,
      action: { libelle: 'Voir le panier', faire: () => ouvrirPanier(true) },
    });
  };

  return (
    <main id="contenu" className="mx-auto max-w-[1280px] px-5 pb-32 pt-6 sm:px-6 lg:pb-20">
      <Fil fils={[
        { libelle: 'La boutique', vers: '/boutique' },
        { libelle: p.familleNom, vers: `/boutique/${p.famille.split('/').pop()}` },
        { libelle: p.nom },
      ]} />

      <div className="grid gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
        {/* ------------------------------------------------------- galerie */}
        <div className="lg:sticky lg:top-[120px] lg:self-start">
          <div className="relative overflow-hidden rounded-[24px]">
            <Media src={photos[vue]?.url || p.photo} alt={photos[vue]?.alt ?? p.nom}
              teinte={p.famille.includes('boeuf') ? 'wine' : p.famille.includes('volailles') ? 'olive' : 'sand'}
              ratio="aspect-[4/5]" monogramme={p.nom.slice(0, 1)} priority />
            <button type="button" onClick={() => basculerFavori(p.id)} aria-pressed={favori}
              className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-paper/90 text-ink-soft shadow-soft backdrop-blur transition hover:text-wine"
              aria-label={favori ? 'Retirer des favoris' : 'Ajouter aux favoris'}>
              <Icon name="heart" size={19} filled={favori} className={favori ? 'text-wine' : ''} />
            </button>
            {p.labels.length > 0 && (
              <div className="absolute left-4 top-4 flex flex-wrap gap-1.5">
                {p.labels.map(l => (
                  <span key={l} className="rounded-full bg-paper/92 px-2.5 py-1 text-[0.6875rem] font-semibold shadow-soft backdrop-blur">{l}</span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3" role="group" aria-label="Autres vues">
            {photos.slice(0, 3).map((ph, i) => (
              <button key={i} type="button" onClick={() => setVue(i)} aria-pressed={vue === i}
                aria-label={`Voir : ${ph.legende || ph.alt}`}
                className={cn('overflow-hidden rounded-xl transition',
                  vue === i ? 'ring-2 ring-wine ring-offset-2 ring-offset-paper' : 'opacity-80 hover:opacity-100')}>
                <Media src={ph.url || p.photo} alt={ph.alt} ratio="aspect-square" monogramme={p.nom.slice(0, 1)} />
              </button>
            ))}
          </div>
        </div>

        {/* -------------------------------------------------------- informations */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/boutique/${p.famille.split('/').pop()}`} className="eyebrow hover:text-ink">{p.familleNom}</Link>
            {p.race && <><span className="text-linen-deep" aria-hidden="true">·</span><span className="text-[0.6875rem] uppercase tracking-[0.14em] text-muted">{p.race}</span></>}
            {p.maturationJours != null && p.maturationJours > 0 && <><span className="text-linen-deep" aria-hidden="true">·</span>
              <span className="text-[0.6875rem] uppercase tracking-[0.14em] text-muted">Maturée {p.maturationJours} j</span></>}
          </div>

          <h1 className="mt-2 text-[clamp(1.9rem,4.4vw,2.9rem)]">{p.nom}</h1>
          <p className="mt-3 max-w-xl text-[1.0625rem] leading-relaxed text-ink-soft">{p.description}</p>

          <div className="mt-4 flex items-center gap-2.5">
            <Note note={REGLAGES.marque.note} />
            <a href="#avis" className="text-sm text-muted underline underline-offset-4 hover:text-ink">Lire les avis</a>
          </div>

          {/* Prix — le grand chiffre est toujours le prix de référence :
              au kilo pour une pièce pesée, prix du format sinon. */}
          <div className="mt-6 flex flex-wrap items-end gap-x-3 gap-y-1">
            <p className="font-display text-[2.4rem] leading-none tracking-tight tabulaire">
              {eur(p.modele === 'AU_KG' ? (p.prixKg ?? 0) : prixUnitaire)}
              <span className="ml-1 text-base font-normal text-muted">
                {p.modele === 'AU_KG' ? 'le kilo' : estFormats ? `les ${kg(poidsFinal)}` : p.modele === 'COLIS' ? 'le colis' : 'la pièce'}
              </span>
            </p>
            <p className="pb-1 text-sm text-muted">
              {p.modele === 'AU_KG'
                ? <>≈ <strong className="font-semibold tabulaire">{eur(prixUnitaire)}</strong> pour {kg(poidsFinal)}</>
                : estFormats
                  ? <>= {eur((prixUnitaire / poidsFinal) * 1000)} le kilo</>
                  : <>poids de référence {kg(poidsFinal)}</>}
            </p>
          </div>
          <p className="mt-1.5 flex items-center gap-1.5 text-[0.8125rem] text-muted">
            <Icon name="scale" size={14} />
            {p.modele === 'AU_KG'
              ? <>Prix au kilo affiché. Le montant final est ajusté à la pesée, tolérance ±{REGLAGES.poids.tolerancePct} % : au-delà, la maison absorbe l’écart.</>
              : <>Prix {estFormats ? 'du format' : 'de la pièce'} affiché. Le poids réel peut varier de ±{REGLAGES.poids.tolerancePct} % ; au-delà, la maison absorbe l’écart.</>}
          </p>

          {/* ------------------------------------------------- sélecteur */}
          <div className="mt-6 rounded-[22px] bg-linen p-4 sm:p-5">
            {!estPiece && !estFormats && (
              <Segmente bloc libelle="Mode de quantité"
                valeur={modeChoix}
                onChange={v => setModeChoix(v as 'POIDS' | 'PERSONNES')}
                options={[
                  { valeur: 'POIDS', libelle: 'Au poids' },
                  { valeur: 'PERSONNES', libelle: 'Par personne' },
                ]} />
            )}

            {estFormats && (
              <div>
                <p className="mb-2 text-sm font-semibold text-ink">Choisissez le format</p>
                <div className="flex flex-wrap gap-2">
                  {p.formats!.map(f => (
                    <button key={f.libelle} type="button" onClick={() => setPoids(f.poidsG)}
                      aria-pressed={poidsFinal === f.poidsG}
                      className="option option--format">
                      <span className="font-semibold">{f.libelle}</span>
                      <span className="option__prix tabular-nums">{eur(f.prix)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {modeChoix === 'POIDS' && !estPiece && !estFormats && (
              <div className="mt-4">
                <div className="flex items-end justify-between">
                  <p className="text-sm font-semibold text-ink">Poids souhaité</p>
                  <p className="font-display text-2xl tabular-nums" aria-live="polite">{kg(poids)}</p>
                </div>
                <input type="range" className="slider mt-2 w-full" min={p.portion.minG}
                  max={p.portion.maxG} step={p.portion.pasG} value={poids}
                  aria-label="Poids souhaité en grammes"
                  aria-valuetext={`${poids} grammes`}
                  onChange={e => setPoids(Number(e.target.value))}
                  style={{ ['--slider-fill' as string]: `linear-gradient(90deg, var(--bordeaux-700) ${((poids - p.portion.minG) / Math.max(1, p.portion.maxG - p.portion.minG)) * 100}%, var(--lin-200) 0)` }} />
                <div className="flex justify-between text-xs text-muted">
                  <span>{kg(p.portion.minG)}</span>
                  <span>{kg(p.portion.maxG)}</span>
                </div>
                <p className="mt-2 text-[0.8125rem] text-muted">
                  Soit environ {Math.max(1, Math.round(poids / (p.portion.gParPersonne || 200)))} personne(s).
                </p>
              </div>
            )}

            {modeChoix === 'PERSONNES' && !estPiece && !estFormats && (
              <div className="mt-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-ink">Nombre de convives</p>
                    <p className="mt-0.5 text-[0.8125rem] text-muted">
                      {p.portion.gParPersonne} g par personne · {kg(poidsFinal)} au total
                    </p>
                  </div>
                  <Compteur valeur={personnes} min={1} max={30} libelle="convives" onChange={setPersonnes} />
                </div>

                <div className="mt-4">
                  <p className="mb-2 text-[0.8125rem] font-medium text-ink-soft">Appétit</p>
                  <Segmente libelle="Appétit" valeur={appetit}
                    onChange={v => setAppetit(v as 'PETIT' | 'NORMAL' | 'GROS')}
                    options={[
                      { valeur: 'PETIT', libelle: 'Petit (−20 %)' },
                      { valeur: 'NORMAL', libelle: 'Normal' },
                      { valeur: 'GROS', libelle: 'Gros (+25 %)' },
                    ]} />
                </div>

                <div className="mt-4">
                  <CaseACocher label="Les enfants comptent pour ½ part"
                    checked={enfants > 0} onChange={e => setEnfants(e.target.checked ? 1 : 0)} />
                  {enfants > 0 && (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-surface/70 px-3 py-2">
                      <span className="text-[0.8125rem] text-ink-soft">Enfants à table</span>
                      <Compteur valeur={enfants} min={1} max={Math.max(1, personnes - 1)} taille="sm"
                        libelle="enfants" onChange={setEnfants} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {estPiece && (
              <p className="text-sm text-muted">
                Vendu à la pièce{p.poidsG ? ` — environ ${kg(p.poidsG)}` : ''}. Le poids exact est confirmé à la pesée.
              </p>
            )}

            {p.options.map(groupe => (
              <div key={groupe.cle} className="mt-5">
                <p className="mb-2 text-sm font-semibold text-ink">{NOMS_OPTIONS[groupe.cle] ?? groupe.cle.toLowerCase()}</p>
                <div className="flex flex-wrap gap-2">
                  {groupe.valeurs.map(v => (
                    <button key={v} type="button" aria-pressed={options[groupe.cle] === v}
                      onClick={() => setOptions(o => ({ ...o, [groupe.cle]: v }))}
                      className="option">
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-5">
              <ZoneTexte label="Un mot pour le boucher (facultatif)" maxLength={200}
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Épaisseur, parage, ficelage, heure de passage…"
                aide={`${note.length}/200 caractères — imprimé en gras sur le ticket de découpe.`} />
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Compteur valeur={quantite} min={1} max={20} libelle="quantité" onChange={setQuantite} />
              <Bouton bloc taille="lg" onClick={ajouterAuPanier}>
                Ajouter — {eur(total)}
              </Bouton>
            </div>

            <p className="mt-3 flex items-center gap-1.5 text-[0.8125rem] text-muted">
              <Icon name="info" size={14} />
              {ar.montant > 0
                ? `Arrhes de ${eur(ar.montant)} à la commande, solde au retrait.`
                : 'Aucun paiement aujourd’hui : vous réglez au retrait après pesée.'}
            </p>
          </div>

          {/* Réassurance */}
          <ul className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { icone: 'knife' as const, t: 'Découpé le matin même' },
              { icone: 'store' as const, t: 'Retrait gratuit sous 24 h' },
              { icone: 'snow' as const, t: `Livraison ${REGLAGES.livraison.temperature}` },
            ].map(x => (
              <li key={x.t} className="flex items-center gap-2 text-[0.8125rem] text-ink-soft">
                <Icon name={x.icone} size={17} className="shrink-0 text-wine" /> {x.t}
              </li>
            ))}
          </ul>

          {/* Détails */}
          <div className="mt-8">
            <Accordéon titre="Origine & traçabilité" ouvertParDefaut>
              <ul className="space-y-2">
                {p.origineDetail?.ne && <li><strong className="font-semibold text-ink">Né en :</strong> {p.origineDetail.ne}</li>}
                {p.origineDetail?.eleve && <li><strong className="font-semibold text-ink">Élevé en :</strong> {p.origineDetail.eleve}</li>}
                {p.origineDetail?.abattu && <li><strong className="font-semibold text-ink">Abattu en :</strong> {p.origineDetail.abattu}</li>}
                {p.origineDetail?.decoupe && <li><strong className="font-semibold text-ink">Découpé en :</strong> {p.origineDetail.decoupe}</li>}
                {!p.origineDetail && p.origine && <li><strong className="font-semibold text-ink">Origine :</strong> {p.origine}</li>}
                {p.race && <li><strong className="font-semibold text-ink">Race :</strong> {p.race}{p.bms ? ` · persillé BMS ${p.bms}` : ''}</li>}
                {p.maturationJours != null && p.maturationJours > 0 && (
                  <li><strong className="font-semibold text-ink">Maturation :</strong> {p.maturationJours} jours en cabinet, sur place.</li>
                )}
                <li><strong className="font-semibold text-ink">Découpe :</strong> atelier de Sannois, à la commande.</li>
              </ul>
            </Accordéon>
            <Accordéon titre="Cuisson & conservation">
              <ul className="space-y-2">
                {p.cuisson?.length ? <li><strong className="font-semibold text-ink">Cuissons conseillées :</strong> {p.cuisson.join(', ')}.</li> : null}
                {p.temperatures && (
                  <li><strong className="font-semibold text-ink">Températures à cœur :</strong>{' '}
                    {Object.entries(p.temperatures).map(([k, v]) => `${NOMS_TEMPERATURE[k] ?? k} ${v} °C`).join(' · ')}.
                  </li>
                )}
                {p.conservation && <li><strong className="font-semibold text-ink">Conservation :</strong> {p.conservation}</li>}
              </ul>
            </Accordéon>
            {aDesMentions && (
              <Accordéon titre="Composition & allergènes">
                {p.composition && (
                  <ul className="mb-3 space-y-1">
                    {p.composition.map(c => (
                      <li key={c.nom} className="flex justify-between gap-4"><span>{c.nom}</span><span className="text-muted">{kg(c.poidsG ?? c.weightG ?? 0)}</span></li>
                    ))}
                  </ul>
                )}
                {p.ingredients && <p><strong className="font-semibold text-ink">Ingrédients :</strong> {p.ingredients}</p>}
                <p className="mt-2">
                  <strong className="font-semibold text-ink">Allergènes :</strong>{' '}
                  {p.allergenes.length ? p.allergenes.join(', ') : 'aucun des quatorze allergènes majeurs n’est déclaré pour cette pièce.'}
                </p>
              </Accordéon>
            )}
          </div>

          {/* Conseil du boucher */}
          <figure className="mt-7 rounded-[20px] border-l-2 border-laiton-500 bg-brass-wash/60 px-5 py-4">
            <p className="eyebrow text-brass">Le conseil du boucher</p>
            <blockquote className="mt-2 font-display text-[1.15rem] leading-snug text-ink">« {p.conseil} »</blockquote>
            <figcaption className="mt-2 text-[0.75rem] text-muted">Michel Duval, gérant — Sannois</figcaption>
          </figure>
        </div>
      </div>

      {/* ------------------------------------------------------------ avis */}
      <section id="avis" className="mt-16 scroll-mt-28 border-t border-linen-deep/70 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Avis vérifiés</p>
            <h2 className="mt-2 text-2xl sm:text-3xl">Ce qu’en disent nos clients</h2>
          </div>
          <div className="flex items-center gap-3 rounded-full bg-surface px-4 py-2 shadow-soft">
            <Note note={REGLAGES.marque.note} />
            <span className="text-sm text-muted">{REGLAGES.marque.avis} avis</span>
          </div>
        </div>
        {avisDuRayon.length > 0 ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {avisDuRayon.slice(0, 3).map(a => (
              <figure key={a.id} className="rounded-[20px] bg-surface p-5 shadow-soft">
                <Note note={a.note} compact />
                <p className="mt-3 font-display text-lg leading-snug">« {a.titre} »</p>
                <p className="mt-2 text-sm leading-relaxed text-muted">{a.texte}</p>
                <figcaption className="mt-3 text-xs text-muted">
                  {a.auteur} · {a.ville}
                  {a.produit !== p.slug && nomParSlug.get(a.produit) && (
                    <> · <span className="text-ink-soft">{nomParSlug.get(a.produit)}</span></>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p className="mt-6 rounded-2xl bg-surface px-5 py-6 text-sm text-muted">
            Pas encore d’avis publié sur ce rayon. La maison tient une moyenne de{' '}
            <strong className="font-semibold text-ink">{REGLAGES.marque.note}/5</strong> sur{' '}
            {REGLAGES.marque.avis} avis vérifiés — et le comptoir prend volontiers le vôtre, par téléphone
            ou au retrait.
          </p>
        )}
      </section>

      {/* -------------------------------------------------------- similaires */}
      {similaires.length > 0 && (
        <section className="mt-16">
          <div className="flex items-end justify-between gap-4">
            <h2 className="text-2xl sm:text-3xl">Dans le même esprit</h2>
            <Link to={`/boutique/${p.famille.split('/').pop()}`} className="text-sm font-semibold underline underline-offset-4">
              Toute la famille
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 lg:grid-cols-4">
            {similaires.map(s => <ProductCard key={s.id} produit={s} />)}
          </div>
        </section>
      )}

      {/* Barre d'achat mobile — toujours accessible, jamais par-dessus le contenu */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-linen-deep bg-paper/96 px-4 py-3 backdrop-blur lg:hidden"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[0.6875rem] text-muted">{estPiece ? 'À la pièce' : kg(poidsFinal)}</p>
            <p className="font-display text-xl leading-none">{eur(total)}</p>
          </div>
          <Bouton onClick={ajouterAuPanier} className="shrink-0">Ajouter au panier</Bouton>
        </div>
      </div>
    </main>
  );
}
